import React, { useState, useEffect } from "react";
import {
  Box, Typography, Card, CardContent,
  CircularProgress, Button, Alert, Chip
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { getMe, getPerformance, runOrchestrator, getSuggestions, getTasks } from "../services/api";

const StatCard = ({ icon, label, value, color }) => (
  <Card sx={{
    background: "rgba(13,20,36,0.8)",
    border: `1px solid ${color}33`,
    borderRadius: "14px",
    position: "relative",
    overflow: "hidden",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: `0 8px 30px ${color}22` }
  }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{
        width: 40, height: 40, borderRadius: "10px",
        background: `${color}18`, border: `1px solid ${color}33`,
        display: "flex", alignItems: "center", justifyContent: "center", mb: 2
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
      </Box>
      <Typography variant="h4" fontWeight={700} color="white" letterSpacing="-1px">
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }} mt={0.5} fontSize={13}>
        {label}
      </Typography>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [stats, setStats] = useState({ suggestions: 0, tasks: 0 });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const role = localStorage.getItem("role");

  useEffect(() => {
    const load = async () => {
      try {
        const role = localStorage.getItem("role");
        const calls = [getMe(), getTasks()];
        if (role === "owner") calls.splice(1, 0, getSuggestions());
        const results = await Promise.all(calls);
        const meRes = results[0];
        const taskRes = role === "owner" ? results[2] : results[1];
        const sugRes = role === "owner" ? results[1] : { data: [] };
        setUser(meRes.data);
        setStats({ suggestions: sugRes.data.length, tasks: taskRes.data.length });
        if (role === "owner") {
          const perfRes = await getPerformance();
          setPerformance(perfRes.data);
        }
      } catch {
        setError("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleRun = async () => {
    setRunning(true);
    setResult(null);
    setError("");
    try {
      const res = await runOrchestrator();
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Orchestrator failed");
    } finally {
      setRunning(false);
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress sx={{ color: "#6366f1" }} />
    </Box>
  );

  return (
    <Box>
      <Box mb={4} sx={{ pb: 2 }}>
        <Typography variant="h4" fontWeight={700} sx={{ color: "#fff !important", letterSpacing: "-0.8px" }}>
          Welcome back, {user?.name} 👋
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.6)", mt: 0.5, fontSize: 15 }}>
          Here is what is happening with your store today
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{
          mb: 3, borderRadius: 2,
          background: "rgba(239,68,68,0.1)",
          border: "1px solid rgba(239,68,68,0.2)",
          color: "#fca5a5"
        }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, mb: 4 }}>
        <StatCard icon={<LightbulbIcon />} label="Pending Suggestions" value={stats.suggestions} color="#6366f1" />
        <StatCard icon={<AssignmentIcon />} label={role === "owner" ? "Total Tasks" : "My Tasks"} value={stats.tasks} color="#8b5cf6" />
        <StatCard icon={<TrendingUpIcon />} label="System Status" value="Live" color="#10b981" />
      </Box>

      {role === "owner" && (
        <>
          <Card sx={{
            background: "rgba(13,20,36,0.8)",
            border: "1px solid rgba(99,102,241,0.2)",
            borderRadius: "14px", mb: 3,
            position: "relative", overflow: "hidden"
          }}>
            <Box sx={{
              position: "absolute", top: 0, left: 0, right: 0, height: 2,
              background: "linear-gradient(90deg, #6366f1, #8b5cf6, transparent)"
            }} />
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: "10px",
                    background: "rgba(99,102,241,0.15)",
                    border: "1px solid rgba(99,102,241,0.3)",
                    display: "flex", alignItems: "center", justifyContent: "center"
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 20, color: "#6366f1" }} />
                  </Box>
                  <Box>
                    <Typography color="#fff" fontWeight={600} fontSize={15}>
                      AI Orchestrator
                    </Typography>
                    <Typography color="rgba(255,255,255,0.35)" fontSize={13}>
                      Analyze store state and generate suggestions
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleRun}
                  disabled={running}
                  startIcon={running
                    ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                    : <PlayArrowIcon />
                  }
                  sx={{
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    borderRadius: "10px", fontWeight: 600, px: 3,
                    boxShadow: "0 4px 15px rgba(99,102,241,0.35)",
                    "&:hover": { boxShadow: "0 6px 20px rgba(99,102,241,0.5)" }
                  }}
                >
                  {running ? "Analyzing..." : "Run Analysis"}
                </Button>
              </Box>

              {result && (
                <Box sx={{
                  mt: 3, p: 2.5, borderRadius: "10px",
                  background: "rgba(0,0,0,0.3)",
                  border: "1px solid rgba(255,255,255,0.06)"
                }}>
                  {result.status === "no_action_required" && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981" }} />
                      <Typography color="#10b981" fontSize={14} fontWeight={600}>
                        All metrics green — no action required
                      </Typography>
                    </Box>
                  )}
                  {result.status === "data_integrity_alert" && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b" }} />
                      <Typography color="#f59e0b" fontSize={14}>{result.reason}</Typography>
                    </Box>
                  )}
                  {result.suggestion_id && (
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip label={result.priority?.toUpperCase()} size="small" sx={{
                          background: "rgba(99,102,241,0.2)", color: "#a5b4fc",
                          fontSize: 10, fontWeight: 700, height: 20
                        }} />
                        <Typography color="rgba(255,255,255,0.4)" fontSize={12}>
                          {result.suggestion_id}
                        </Typography>
                      </Box>
                      <Typography color="#fff" fontSize={14} fontWeight={500} mb={1}>
                        {result.problem}
                      </Typography>
                      <Typography color="rgba(255,255,255,0.35)" fontSize={13}>
                        Confidence: {result.confidence_score}%
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {performance && (
            <Card sx={{
              background: "rgba(13,20,36,0.8)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: "14px"
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography color="#fff" fontWeight={600} fontSize={15} mb={2}>
                  Performance Summary
                </Typography>
                <Typography color="rgba(255,255,255,0.4)" fontSize={13} sx={{
                  whiteSpace: "pre-line",
                  fontFamily: "monospace"
                }}>
                  {performance.performance_summary === "No performance history yet."
                    ? "No performance history yet — run the orchestrator to start tracking."
                    : performance.performance_summary}
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
